import React, { useState, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown, Trash2, Plus, AlertCircle, RefreshCw, Search } from 'lucide-react';
import { colors } from '../../constants/theme';
import { matchesService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';

const MatchQueue = ({ onQueueUpdate }) => {
  const [queue, setQueue] = useState([]);
  const [readyMatches, setReadyMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterMatches();
  }, [readyMatches, searchTerm, selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Carregando dados da fila...');

      // Carregar fila e partidas prontas - SEM FALLBACKS
      const [queueResponse, readyResponse] = await Promise.allSettled([
        matchesService.getQueue(),
        matchesService.getReady()
      ]);

      console.log('📋 Queue response:', queueResponse);
      console.log('⚽ Ready matches response:', readyResponse);

      // Processar resposta da fila
      if (queueResponse.status === 'fulfilled') {
        if (queueResponse.value.success) {
          setQueue(queueResponse.value.data || []);
        } else {
          throw new Error(`Erro na fila: ${queueResponse.value.error?.message || 'Resposta inválida'}`);
        }
      } else {
        console.error('❌ Erro ao carregar fila:', queueResponse.reason);
        throw new Error(`Erro ao carregar fila: ${queueResponse.reason.error?.message || queueResponse.reason.message || 'Erro desconhecido'}`);
      }
      
      // Processar resposta das partidas prontas
      if (readyResponse.status === 'fulfilled') {
        if (readyResponse.value.success) {
          const allReady = readyResponse.value.data || [];
          const queueIds = (queueResponse.status === 'fulfilled' && queueResponse.value.data) 
            ? queueResponse.value.data.map(q => q.id) 
            : [];
          
          // Filtrar partidas que não estão na fila
          const availableMatches = allReady.filter(m => !queueIds.includes(m.id));
          setReadyMatches(availableMatches);
        } else {
          throw new Error(`Erro nas partidas prontas: ${readyResponse.value.error?.message || 'Resposta inválida'}`);
        }
      } else {
        console.error('❌ Erro ao carregar partidas prontas:', readyResponse.reason);
        throw new Error(`Erro ao carregar partidas prontas: ${readyResponse.reason.error?.message || readyResponse.reason.message || 'Erro desconhecido'}`);
      }

    } catch (error) {
      console.error('❌ Erro ao carregar dados da fila:', error);
      setError(error.message || 'Erro ao carregar fila de partidas');
      setQueue([]);
      setReadyMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const filterMatches = () => {
    let filtered = readyMatches;

    // Filtrar por categoria
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(match => 
        match.player1?.category === selectedCategory || 
        match.player2?.category === selectedCategory
      );
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(match => 
        match.player1?.name?.toLowerCase().includes(term) ||
        match.player2?.name?.toLowerCase().includes(term) ||
        match.phase?.toLowerCase().includes(term)
      );
    }

    setFilteredMatches(filtered);
  };

  const handleAddToQueue = async (matchId, priority = 0) => {
    setActionLoading(true);
    try {
      console.log(`➕ Adicionando partida ${matchId} à fila com prioridade ${priority}`);
      
      const response = await matchesService.addToQueue(matchId, priority);
      
      if (response.success) {
        console.log('✅ Partida adicionada à fila');
        await loadData();
        onQueueUpdate && onQueueUpdate();
        
        // Feedback visual
        const matchName = readyMatches.find(m => m.id === matchId);
        const playerNames = matchName ? 
          `${matchName.player1?.name} vs ${matchName.player2?.name}` : 
          `Partida ${matchId}`;
        alert(`✅ ${playerNames} foi adicionada à fila!`);
      } else {
        throw new Error(response.error?.message || 'Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao adicionar à fila:', error);
      alert(`❌ Erro ao adicionar partida à fila:\n${error.error?.message || error.message || 'Erro desconhecido'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFromQueue = async (matchId) => {
    setActionLoading(true);
    try {
      console.log(`➖ Removendo partida ${matchId} da fila`);
      
      const response = await matchesService.removeFromQueue(matchId);
      
      if (response.success) {
        console.log('✅ Partida removida da fila');
        await loadData();
        onQueueUpdate && onQueueUpdate();
      } else {
        throw new Error(response.error?.message || 'Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao remover da fila:', error);
      alert(`❌ Erro ao remover da fila:\n${error.error?.message || error.message || 'Erro desconhecido'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearQueue = async () => {
    if (!window.confirm('Tem certeza que deseja limpar toda a fila?')) {
      return;
    }

    setActionLoading(true);
    try {
      console.log('🗑️ Limpando fila completa');
      
      const response = await matchesService.clearQueue();
      
      if (response.success) {
        console.log('✅ Fila limpa');
        await loadData();
        onQueueUpdate && onQueueUpdate();
        alert('✅ Fila limpa com sucesso!');
      } else {
        throw new Error(response.error?.message || 'Resposta inválida da API');
      }
    } catch (error) {
      console.error('❌ Erro ao limpar fila:', error);
      alert(`❌ Erro ao limpar fila:\n${error.error?.message || error.message || 'Erro desconhecido'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const updatePriority = async (matchId, newPriority) => {
    await handleAddToQueue(matchId, newPriority);
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Carregando fila..." />;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <ErrorMessage 
          message={`Erro ao carregar fila: ${error}`} 
          onRetry={loadData} 
        />
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
            <h4 className="font-medium text-yellow-800">Possíveis soluções:</h4>
          </div>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Verifique se o backend está rodando em http://localhost:3001</li>
            <li>• Confirme se os endpoints da fila estão implementados no backend</li>
            <li>• Verifique os logs do console para mais detalhes</li>
            <li>• Tente fazer login novamente se houver erro de autenticação</li>
          </ul>
        </div>
      </div>
    );
  }

  const categories = ['all', 'mirim', 'infantil', 'juvenil', 'adulto', 'master'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Gerenciar Fila de Partidas
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={loadData}
            disabled={loading || actionLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
          {queue.length > 0 && (
            <button
              onClick={handleClearQueue}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg disabled:opacity-50"
            >
              Limpar Fila
            </button>
          )}
        </div>
      </div>

      {/* Debug Info */}
      <div className="bg-gray-50 border rounded-lg p-3">
        <div className="text-sm text-gray-600">
          <strong>Status:</strong> Fila: {queue.length} | Disponíveis: {readyMatches.length} | Filtradas: {filteredMatches.length}
        </div>
      </div>

      {/* Resto do componente igual ao anterior... */}
      {/* (Fila atual, Partidas disponíveis, etc.) */}
      
      {/* Fila atual */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b" style={{ backgroundColor: colors.primary }}>
          <h3 className="text-white font-semibold">
            Fila Atual ({queue.length} partidas)
          </h3>
        </div>
        
        {queue.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">Nenhuma partida na fila</p>
            <p className="text-sm">Adicione partidas disponíveis abaixo</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {queue.map((queuedMatch, index) => (
              <div key={queuedMatch.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-center">
                      <span className="text-xs text-gray-500">#{index + 1}</span>
                      <span className="text-lg font-bold" style={{ color: colors.primary }}>
                        {index + 1}º
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <p className="font-medium">{queuedMatch.player1?.name || 'Jogador 1'}</p>
                          <p className="text-xs text-gray-500">{queuedMatch.player1?.category || 'N/A'}</p>
                        </div>
                        
                        <div className="text-center">
                          <p className="text-lg font-bold text-gray-500">VS</p>
                          <p className="text-xs text-gray-400">{queuedMatch.phase}</p>
                        </div>
                        
                        <div className="text-center">
                          <p className="font-medium">{queuedMatch.player2?.name || 'Jogador 2'}</p>
                          <p className="text-xs text-gray-500">{queuedMatch.player2?.category || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Controles de prioridade */}
                    <div className="flex flex-col">
                      <button
                        onClick={() => updatePriority(queuedMatch.id, (queuedMatch.priority || 0) + 1)}
                        disabled={actionLoading || index === 0}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        title="Mover para cima"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => updatePriority(queuedMatch.id, Math.max(0, (queuedMatch.priority || 0) - 1))}
                        disabled={actionLoading || index === queue.length - 1}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        title="Mover para baixo"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemoveFromQueue(queuedMatch.id)}
                      disabled={actionLoading}
                      className="p-2 text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Remover da fila"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {(queuedMatch.priority || 0) > 0 && (
                  <div className="mt-2 text-xs text-blue-600">
                    Prioridade: {queuedMatch.priority}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Partidas disponíveis para adicionar */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-800">
              Partidas Disponíveis ({filteredMatches.length})
            </h3>
            <span className="text-sm text-gray-500">
              Total: {readyMatches.length} partidas
            </span>
          </div>

          {/* Filtros */}
          <div className="flex space-x-4">
            {/* Busca por nome */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por jogador ou fase..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: colors.primary }}
                />
              </div>
            </div>

            {/* Filtro por categoria */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ focusRingColor: colors.primary }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'Todas as categorias' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {filteredMatches.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {readyMatches.length === 0 ? (
              <>
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">Nenhuma partida disponível</p>
                <p className="text-sm">Verifique se há partidas prontas no backend</p>
              </>
            ) : (
              <>
                <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">Nenhuma partida encontrada</p>
                <p className="text-sm">Tente ajustar os filtros de busca</p>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredMatches.map((match) => (
              <div key={match.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <p className="font-medium">{match.player1?.name || 'Jogador 1'}</p>
                      <p className="text-xs text-gray-500">{match.player1?.category || 'N/A'}</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-500">VS</p>
                      <p className="text-xs text-gray-400">{match.phase}</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-medium">{match.player2?.name || 'Jogador 2'}</p>
                      <p className="text-xs text-gray-500">{match.player2?.category || 'N/A'}</p>
                    </div>

                    <div className="text-xs text-gray-500">
                      ID: {match.id}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleAddToQueue(match.id, 1)}
                      disabled={actionLoading}
                      className="flex items-center space-x-2 px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded text-sm disabled:opacity-50"
                      title="Adicionar com prioridade alta"
                    >
                      <ChevronUp className="h-3 w-3" />
                      <span>Prioridade</span>
                    </button>

                    <button
                      onClick={() => handleAddToQueue(match.id, 0)}
                      disabled={actionLoading}
                      className="flex items-center space-x-2 px-4 py-2 text-white rounded font-medium hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Plus className="h-4 w-4" />
                      <span>Adicionar à Fila</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Informações da fila */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center mb-2">
          <AlertCircle className="h-5 w-5 text-blue-500 mr-2" />
          <h4 className="font-medium text-blue-800">Como funciona:</h4>
        </div>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Busque e filtre</strong> partidas disponíveis</li>
          <li>• <strong>Adicione à fila</strong> com prioridade normal ou alta</li>
          <li>• <strong>Reordene</strong> usando as setas ↑↓</li>
          <li>• <strong>Remova</strong> partidas desnecessárias</li>
          <li>• Máximo de 2 partidas simultâneas</li>
        </ul>
      </div>
    </div>
  );
};

export default MatchQueue;